import { iono } from 'global';

/**
 * Get a formatted time remaining string
 *
 * @param  {number} position Current advertisement position
 * @param  {number} duration Advertisement duration
 * @return {string}
 */
export function getFormattedTimeRemaining (position, duration) {
  return '-' + iono.Player.formatSeconds(duration - position);
}

/**
 * Get a companion ad image for an advertisement matching the dimensions given
 *
 * @param  {object} advert An advertisement with optional companion ads
 * @param  {number} width  The desired image width
 * @param  {number} height The desired image height
 * @return {string}
 */
export function getCompanionImage (advert, width, height) {
  if (!(width && height)) {
    throw new Error('Cannot get companion image, you must supply a target width and height');
  }

  const companionImages = ((advert && advert.companions) || []).filter(function (companion) {
    return (companion.type === 'image' && companion.width === width && companion.height === height) || '';
  });

  if (companionImages.length === 0) {
    return '';
  }

  return companionImages[0].url;
}

/**
 * Get a clickthrough link associated with an advertisement
 *
 * @param  {object} advert An advertisement with an optional clickthrough link
 * @return {string}
 */
export function getClickthroughLink (advert, companion) {
  if (companion && companion.clickthrough) {
    return companion.clickthrough;
  }
  if (advert && advert.metadata && advert.metadata.url) {
    return advert.metadata.url;
  }
  return '';
}

/**
 * Get the ui text to display for a clickthrough link
 *
 * @param  {object} advert An advertisement
 * @param  {object} locale An optional locale object to localise the return string
 * @return {string}
 */
export function getClickthroughLinkText (advert, locale) {
  if (advert && advert.metadata) {
    if (advert.metadata.description) {
      return advert.metadata.description;
    }
    if (advert.metadata.title) {
      return advert.metadata.title;
    }
  }

  if (locale && locale.advertisement) {
    return locale.advertisement;
  }

  return 'Advertisement';
}

/**
 * Calculate the time remaining before the user can skip the current advertisement
 *
 * @param  {number} skipOffset The time to wait before skipping becomes available
 * @param  {number} position   The amount of time progressed for the current ad
 * @return {number}
 */
export function calculateRemainingSkipOffset (skipOffset, position) {
  return Math.ceil(Math.max(0, Math.min(skipOffset, skipOffset - position)));
}

/**
 * Get the text to display on the skip button
 *
 * @param  {number} skipOffset The time to wait before skipping becomes available
 * @param  {number} position   The amount of time progressed for the current ad
 * @param  {object} locale An optional locale object to localise the return string
 * @return {string}
 */
export function getSkipButtonText (skipOffset, position, locale) {
  if (skipOffset === -1) {
    return '';
  }

  const remainder = calculateRemainingSkipOffset(skipOffset, position);

  if (remainder > 0) {
    return `${locale.advertising.skip} ${remainder}`;
  }

  return `${locale.advertising.skip}`;
}

export default {
  getFormattedTimeRemaining,
  getCompanionImage,
  getClickthroughLink,
  getClickthroughLinkText,
  calculateRemainingSkipOffset,
  getSkipButtonText
};
